<template>

    <div v-if="items.length || actions.length" v-click-outside="hideMenu" class="dropdown">

        <slot
            name="toggle"
            :toggle="toggleMenu"
            :show="show"
            :label="toggleLabel"
            :aria-expanded="show ? 'true' : 'false'"
        >
            <v-dropdown-toggle
                :size="size"
                :theme="theme"
                :icon="icon"
                :prefix="prefix"
                :label="toggleLabel"
                :loading="loading"
                :aria-label="ariaLabel"
                :aria-expanded="show ? 'true' : 'false'"
                aria-haspopup="menu"
                @click.native="toggleMenu"
            />
        </slot>

        <transition name="dropdown">
            <div v-show="show" :class="`dropdown-menu menu-${menuPosition}`" :style="menuStyles">
                <div v-if="items.length" class="dropdown-menu-body">
                    <template v-for="(i, index) in items">
                        <div v-if="i.el === 'divider'" :key="`dd-divider-${index}`" :class="itemClasses(i)" />
                        <div v-else-if="itemRowActions(i).length" :key="`dd-item-actions-${index}`" :class="[itemClasses(i), 'has-side-actions']" :title="i.value">
                            <div class="dropdown-item-main" @click.stop="selectItem(i)">
                                <div class="w-100p d-flex justify-content-between align-items-center">
                                    <span class="d-flex align-items-center dropdown-item-label-wrap">
                                        <span v-if="i.icon && !i.icon_repeats" class="mr-5">
                                            <i v-if="i.icon.startsWith('fa')" :class="i.icon" :style="{ color: i.icon_color }" />
                                            <img v-else :src="i.icon" />
                                        </span>
                                        <span v-if="i.icon && i.icon_repeats" class="mr-5">
                                            <i v-for="count in i.icon_repeats"
                                                :key="`icon-${count}`"
                                                :class="i.icon"
                                                :style="{ color: i.icon_color }">
                                            </i>
                                        </span>
                                        <span class="label" :style="{ color: i.color }">{{ i.value | truncate(50) }}</span>
                                    </span>
                                    <span v-if="i.selected" class="ml-5"><i class="fas fa-check" /></span>
                                    <span v-else-if="i.icon_right" class="ml-5 text-muted">
                                        <i :class="i.icon_right" :style="{ color: i.icon_right_color }" />
                                    </span>
                                </div>
                            </div>
                            <div class="dropdown-item-actions">
                                <div
                                    v-for="(a, ai) in itemRowActions(i)"
                                    :key="`dd-item-action-${index}-${ai}`"
                                    class="dropdown-item-action-btn"
                                    :class="{ 'is-danger': a.type === 'danger' }"
                                    :title="a.title"
                                    @click.stop="onItemAction(i, a)">
                                    <i v-if="a.icon" :class="a.icon" :style="{ color: a.icon_color }" />
                                </div>
                            </div>
                        </div>
                        <div v-else :key="`dd-item-plain-${index}`" :class="itemClasses(i)" :title="i.value" @click.stop="selectItem(i)">
                            <div v-if="!i.el || i.el === 'item'" class="w-100p d-flex justify-content-between align-items-center">
                                <span class="d-flex align-items-center">
                                    <span v-if="i.icon && !i.icon_repeats" class="mr-5">
                                        <i v-if="i.icon.startsWith('fa')" :class="i.icon" :style="{ color: i.icon_color }" />
                                        <img v-else :src="i.icon" />
                                    </span>
                                    <span v-if="i.icon && i.icon_repeats" class="mr-5">
                                        <i v-for="count in i.icon_repeats"
                                            :key="`icon-${count}`"
                                            :class="i.icon"
                                            :style="{ color: i.icon_color }">
                                        </i>
                                    </span>
                                    <span class="label" :style="{ color: i.color }">{{ i.value | truncate(50) }}</span>
                                </span>
                                <span v-if="i.selected"><i class="fas fa-check" /></span>
                                <span v-else-if="i.icon_right" class="text-muted">
                                    <i :class="i.icon_right" :style="{ color: i.icon_right_color }" />
                                </span>
                            </div>
                        </div>
                    </template>
                </div>
                <div v-if="actions.length" class="dropdown-menu-footer">
                    <div v-for="(i, index) in actions" :key="`dd-action-${index}`" :class="itemClasses(i)" @click.stop="selectItem(i)">
                        <div class="w-100p d-flex justify-content-between align-items-center">
                            <span class="d-flex align-items-center">
                                <span v-if="i.icon" class="mr-5">
                                    <i v-if="i.icon.startsWith('fa')" :class="i.icon" :style="{ color: i.icon_color }" />
                                    <img v-else :src="i.icon" />
                                </span>
                                <span class="label">{{ i.value | truncate(50) }}</span>
                            </span>
                            <span v-if="i.icon_right" class="text-muted">
                                <i :class="i.icon_right" :style="{ color: i.icon_right_color }" />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

    </div>

</template>

<style scoped lang="scss">

    .dropdown {

        flex-shrink: 0;
        position: relative;

        .dropdown-menu {
            @include shad(2);
            background: $color-white;
            border: 1px solid $color-rule;
            border-radius: 5px;
            box-sizing: border-box;
            display: block;
            left: 0;
            margin: 5px 0 0 0;
            padding: 0 0 5px 0;
            position: absolute;
            z-index: 100;
            &.menu-tl,
            &.menu-tr {
                bottom: 100%;
                margin: 0 0 5px 0;
                top: auto;
            }
            &.menu-bl,
            &.menu-br {
                bottom: auto;
                margin: 5px 0 0 0;
                top: 100%;
            }
            &.menu-br,
            &.menu-tr {
                left: auto;
                right: 0;
            }
        }

        .dropdown-menu-body {
            max-height: 400px;
            max-width: 600px;
            min-width: 200px;
            overflow: auto;
            padding: 10px;
        }

        .dropdown-menu-footer {
            border-top: 1px solid $color-rule;
            padding: 10px;
        }

        .dropdown-item {
            align-items: center;
            background: $color-white;
            border: none;
            border-radius: 3px;
            box-sizing: border-box;
            color: $color-text;
            cursor: pointer;
            display: flex;
            line-height: 0;
            margin: 0;
            min-height: 30px;
            padding: 5px 10px;
            position: relative;
            text-decoration: none;
            transition: all .3s;
            user-select: none;
            &:hover {
                background-color: $color-highlight;
            }
            &.disabled {
                background: $color-white !important;
                cursor: default;
            }
            .label {
                @include font-medium(12px);
                line-height: 1.25;
            }
            i, img {
                font-size: 12px;
                height: auto;
                text-align: center;
                width: 16px;
            }
            &.has-side-actions {
                line-height: normal;
                padding: 0;
                .dropdown-item-main {
                    flex: 1;
                    line-height: normal;
                    min-width: 0;
                    padding: 5px 10px;
                }
                .dropdown-item-label-wrap {
                    flex: 1;
                    min-width: 0;
                    .label {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                }
                .dropdown-item-actions {
                    align-items: center;
                    align-self: stretch;
                    display: flex;
                    flex-shrink: 0;
                    gap: 0;
                    padding: 0 5px;
                }
                .dropdown-item-action-btn {
                    align-items: center;
                    color: $color-muted;
                    cursor: pointer;
                    display: flex;
                    flex-shrink: 0;
                    font-size: 12px;
                    justify-content: center;
                    margin: 0;
                    padding: 0 2px;
                    transition: color .3s;
                    &:hover {
                        color: $color-primary;
                    }
                    &.is-danger:hover {
                        color: $color-danger;
                    }
                }
            }
        }

        .dropdown-divider {
            border-top: 1px solid $color-rule;
            margin: 10px 0;
        }

    }

</style>

<script>

import ClickOutside from 'vue-click-outside';
import DropdownToggle from '@cmp/DropdownToggle';

export default {

    components: {
        'v-dropdown-toggle': DropdownToggle,
    },

    directives: {
        ClickOutside,
    },

    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        fixed: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: 'sm',
        },
        theme: {
            type: String,
            default: 'white',
        },
        items: {
            type: Array,
            required: true,
        },
        actions: {
            type: Array,
            default: () => [],
        },
        icon: {
            type: String,
        },
        prefix: {
            type: String,
            default: '',
        },
        label: {
            type: String,
        },
        selected: {
            type: String,
            default: '',
        },
        showSelected: {
            type: Boolean,
            default: true,
        },
        menuPosition: {
            type: String,
            default: 'bl',
        },
        ariaLabel: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            key: this.selected,
            query: '',
            menuFixed: {},
        }
    },

    computed: {

        show() {
            return this.$store.getters.dropdown === this._uid;
        },

        menuStyles() {
            if (!this.fixed || !Object.keys(this.menuFixed).length) {
                return {};
            }

            return { position: 'fixed', ...this.menuFixed };
        },

        selectedItem() {
            return this.items.find(i => i.key === this.key);
        },

        toggleLabel() {
            return this.showSelected && this.selectedItem ? (this.selectedItem.value ?? this.selectedItem) : this.label;
        },

    },

    watch: {

        selected(val) {
            this.key = val;
        },

        show(val) {
            if (!this.fixed) {
                return;
            }
            if (val) {
                window.addEventListener('scroll', this.hideMenuOnScroll, true);
            } else {
                window.removeEventListener('scroll', this.hideMenuOnScroll, true);
            }
        },

    },

    beforeDestroy() {
        window.removeEventListener('scroll', this.hideMenuOnScroll, true);
    },

    methods: {

        hideMenu() {
            if (this.show) {
                this.$store.commit('setDropdown', null);
            }
        },

        hideMenuOnScroll(e) {
            if (!this.show || (e.target && this.$el.contains(e.target))) {
                return;
            }
            this.hideMenu();
        },

        itemClasses(item) {
            return {
                'dropdown-item': item.el === 'item' || item.el === undefined,
                'dropdown-divider': item.el === 'divider',
                'text-danger': item.type === 'danger',
                'text-primary': item.type === 'primary',
                'text-success': item.type === 'success',
                'disabled': item.disabled === true,
            };
        },

        itemRowActions(item) {
            if (!item || item.disabled === true || item.el === 'divider') {
                return [];
            }
            return Array.isArray(item.actions) ? item.actions : [];
        },

        onItemAction(item, action) {
            if (!action || item.disabled === true || item.el === 'divider') {
                return;
            }
            this.$emit('item-action', { action, item });
            this.hideMenu();
        },

        selectItem(item) {
            if (item.disabled === true || item.el === 'divider') {
                return;
            }
            this.$emit('change', item);
            if (this.showSelected === true) {
                this.key = item.key;
            }
            this.hideMenu();
        },

        toggleMenu() {
            if (this.show) {
                this.$store.commit('setDropdown', null);
            } else {
                if (this.fixed) {
                    const rect = this.$el.getBoundingClientRect();
                    if (this.menuPosition === 'right' || this.menuPosition === 'left') {
                        this.menuFixed = this.menuPosition === 'right'
                            ? {
                                top: 'auto',
                                right: 'auto',
                                bottom: `${window.innerHeight - rect.bottom}px`,
                                left: `${rect.right + 5}px`,
                            }
                            : {
                                top: 'auto',
                                right: `${window.innerWidth - rect.left + 5}px`,
                                bottom: `${window.innerHeight - rect.bottom}px`,
                                left: 'auto',
                            };
                    } else {
                        const opensUp = this.menuPosition.startsWith('t');
                        const opensRight = this.menuPosition.endsWith('r');
                        if (opensUp) {
                            const bottom = `${window.innerHeight - rect.top + 5}px`;
                            this.menuFixed = opensRight
                                ? { top: 'auto', right: `${window.innerWidth - rect.right}px`, bottom, left: 'auto' }
                                : { top: 'auto', right: 'auto', bottom, left: `${rect.left}px` };
                        } else {
                            const top = `${rect.bottom + 5}px`;
                            this.menuFixed = opensRight
                                ? { top, right: `${window.innerWidth - rect.right}px`, left: 'auto' }
                                : { top, left: `${rect.left}px`, right: 'auto' };
                        }
                    }
                }
                this.$store.commit('setDropdown', this._uid);
            }
        },

    },

}

</script>
